import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db, functions } from '../firebase';
import type { PlayerCreditTransaction } from '../types/credits';

// ── Cloud Function wrappers ────────────────────────────────────────────────────

export interface AdminGrantCreditsParams {
  playerId: string;
  amountCents: number;
  reason?: string;
}

export interface AdminGrantCreditsResult {
  success: boolean;
  newBalanceCents: number;
  transactionId: string;
}

export const adminGrantCreditsFn = httpsCallable<
  AdminGrantCreditsParams,
  AdminGrantCreditsResult
>(functions, 'adminGrantCredits');

export interface DebitCreditsParams {
  amountCents: number;
  coachId: string;
  offeringId: string;
}

export interface DebitCreditsResult {
  success: boolean;
  creditTransactionId: string;
  newBalanceCents: number;
}

export const debitCreditsForLessonFn = httpsCallable<
  DebitCreditsParams,
  DebitCreditsResult
>(functions, 'debitCreditsForLesson');

// ── Firestore queries ─────────────────────────────────────────────────────────

/**
 * Returns the player's current credit balance in cents (0 if none).
 */
export async function getPlayerCreditBalance(uid: string): Promise<number> {
  const userSnap = await getDoc(doc(db, 'users', uid));
  return (userSnap.data()?.creditBalanceCents as number | undefined) ?? 0;
}

/**
 * Returns the credit transaction history for a player, newest first.
 */
export async function getPlayerCreditTransactions(
  playerId: string,
  maxResults = 50,
): Promise<PlayerCreditTransaction[]> {
  const q = query(
    collection(db, 'playerCreditTransactions'),
    where('playerId', '==', playerId),
    orderBy('createdAt', 'desc'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as PlayerCreditTransaction);
}

/**
 * Returns all credit grants across all players — for the admin Credits tab.
 * Newest first.
 */
export async function getAllCreditGrants(maxResults = 200): Promise<PlayerCreditTransaction[]> {
  const q = query(
    collection(db, 'playerCreditTransactions'),
    where('type', '==', 'grant'),
    orderBy('createdAt', 'desc'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as PlayerCreditTransaction);
}

/**
 * Returns the total credits issued (all-time sum of admin grants) in cents.
 * Used for the Billing summary "Credits Issued" stat.
 */
export async function getTotalCreditsIssuedCents(): Promise<number> {
  const q = query(
    collection(db, 'playerCreditTransactions'),
    where('type', '==', 'grant'),
    limit(1000),
  );
  const snap = await getDocs(q);
  return snap.docs.reduce((sum, d) => sum + ((d.data().amountCents as number) ?? 0), 0);
}

/**
 * Returns all credit-paid lessons — used for DB-funded payout tracking.
 */
export async function getCreditPaidLessons() {
  const q = query(
    collection(db, 'lessons'),
    where('paymentMethod', '==', 'credits'),
    limit(500),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
